import React from 'react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Heart,
  ShieldCheck,
  Handshake,
  Lightning,
  CurrencyDollar,
  ChatCircle,
  ArrowRight,
  CheckCircle,
  HardHat,
  Buildings,
  House,
  Storefront,
  ShoppingCartSimple,
  Sparkle,
  PaperPlaneTilt,
  Robot,
} from '@phosphor-icons/react';

const audiences = [
  {
    key: 'homeowner',
    icon: House,
    title: 'For Homeowners',
    color: '#2B4C6F',
    hover: '#4A7DA6',
    points: [
      'Get free quotes from vetted local contractors',
      'Describe your project to our AI and it drafts the job for you',
      'Compare bids, reviews and verified credentials side by side',
      'Track progress with photos, milestones and messaging',
      'Pay safely — funds sit in escrow until work is done right',
    ],
    cta: 'Get Free Quotes',
    to: '/get-quotes',
  },
  {
    key: 'contractor',
    icon: Buildings,
    title: 'For Contractors',
    color: '#EA580C',
    hover: '#F97316',
    points: [
      'Fresh homeowner leads and job requests in your trade and city',
      'Post jobs and staff your crew in hours, not weeks',
      'Message workers directly — voice, text, or instant translation',
      'Run projects with milestones, invoices and escrow payouts',
      'Buy and sell tools with other pros on the Marketplace',
    ],
    cta: 'Start Growing Your Business',
    to: '/signup?type=contractor',
  },
  {
    key: 'worker',
    icon: HardHat,
    title: 'For Workers',
    color: '#059669',
    hover: '#10B981',
    points: [
      'Find real jobs in your area — updated daily',
      'Apply with one click and hear back fast',
      'Showcase skills, certifications and your portfolio',
      'Get paid on time with escrow-protected payments',
      'Build your reputation with verified reviews',
    ],
    cta: 'Start Finding Work',
    to: '/signup?type=worker',
  },
];

const platformExtras = [
  { icon: Robot, title: 'AI Project Chat', desc: 'Homeowners describe their project in plain words — our AI asks the right questions, drafts the posting and finds matching pros.', to: '/ai-project' },
  { icon: Storefront, title: 'Tools Marketplace', desc: 'Buy and sell used tools, materials and equipment with other pros. Make an offer, negotiate, done.', to: '/marketplace' },
  { icon: ShoppingCartSimple, title: 'The Tool Store', desc: 'Hand-picked new tools and gear our contractors actually recommend, with direct links to buy.', to: '/store' },
  { icon: PaperPlaneTilt, title: 'Telegram Mini App', desc: 'Run your whole ConstructHelp world from inside Telegram — jobs, messages and alerts on the go.', href: 'https://t.me/Constructhelp123bot' },
  { icon: Sparkle, title: 'Guides & Tutorials', desc: 'Cost guides, hiring guides and weekly expert articles to plan every project with confidence.', to: '/tutorials' },
];

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 transition-colors duration-300" data-testid="about-page">
      {/* Hero Section */}
      <div className="bg-[#0F172A] text-white py-16 border-b-4 border-[#D95800]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Where Great Projects<br />
            <span className="text-[#D95800]">Find Great People</span>
          </h1>
          <p className="text-xl text-neutral-300 max-w-3xl leading-relaxed">
            ConstructHelp brings homeowners, contractors and skilled workers together on one platform —
            with verified profiles, secure escrow payments and AI that does the busywork.
            No endless phone calls. No unreliable referrals. Just the right people, right when you need them.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* The Problem We Solve */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Construction Hiring is Broken
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              We built ConstructHelp because we've lived the frustration ourselves — on both sides of the job site.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                problem: 'Wasted Time',
                description: 'Homeowners chase quotes for weeks. Contractors spend hours calling around for crew. Nobody calls back.',
              },
              {
                problem: 'No Trust',
                description: "You never know what you're getting until someone shows up on site — or doesn't. Reviews and credentials are scattered everywhere.",
              },
              {
                problem: 'Risky Payments',
                description: 'Deposits vanish, invoices go unpaid, and a handshake is the only guarantee anyone gets.',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#D95800]">
                <p className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">{item.problem}</p>
                <p className="text-neutral-600 dark:text-neutral-400">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#D95800]/10 dark:bg-[#D95800]/20 border-2 border-[#D95800] rounded-xl p-8 text-center">
            <p className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              ConstructHelp changes everything.
            </p>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Verified people, real reviews, instant communication and escrow-protected payments — all in one place.
            </p>
          </div>
        </div>

        {/* Our Story */}
        <div className="mb-20">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 md:p-12 shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#D95800]" data-testid="about-story">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#D95800] rounded-xl flex items-center justify-center border-2 border-black">
                <Heart size={24} weight="bold" className="text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white">Why We Built This</h2>
            </div>
            <div className="text-neutral-700 dark:text-neutral-300 text-lg leading-relaxed space-y-4 max-w-4xl">
              <p>
                ConstructHelp started with a simple observation from years around job sites: the hardest part of
                construction isn't the work — it's finding the right people to do it. Homeowners couldn't find
                trustworthy pros. Contractors couldn't find reliable crew. Great workers couldn't find steady jobs.
              </p>
              <p>
                So we built the platform we wished existed: one place where every side of a project meets, with the
                trust, tools and technology that other industries have had for years. Today that means AI that drafts
                your project for you, escrow that protects every dollar, a marketplace for tools, and a community of
                verified pros — and we're just getting started.
              </p>
            </div>
          </div>
        </div>

        {/* Why ConstructHelp */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Why People Choose Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Lightning,
                title: 'Matches in Minutes',
                description: 'Post a job or request quotes and start hearing back immediately. No more waiting days for callbacks.',
              },
              {
                icon: ShieldCheck,
                title: 'Verified Profiles',
                description: 'Real reviews, verified skills, licenses and work history — on every profile, before you commit.',
              },
              {
                icon: CurrencyDollar,
                title: 'Secure Escrow Payments',
                description: 'Milestone-based escrow protects homeowners and guarantees pros get paid when work is done right.',
              },
              {
                icon: Robot,
                title: 'AI That Does the Busywork',
                description: 'From drafting project postings to finding leads, our AI handles the tedious parts so you can build.',
              },
              {
                icon: ChatCircle,
                title: 'Built-In Messaging',
                description: 'Voice messages, instant translation, real-time chat — communication that works on the job site.',
              },
              {
                icon: Handshake,
                title: 'Long-Term Relationships',
                description: 'Find people you can rely on again and again. Build your go-to crew and client base over time.',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#D95800] hover:translate-y-[-4px] hover:shadow-[4px_8px_0px_#000] dark:hover:shadow-[4px_8px_0px_#D95800] transition-[transform,box-shadow]">
                <div className="w-14 h-14 bg-[#0F172A] rounded-xl flex items-center justify-center mb-4 border-2 border-black">
                  <item.icon size={28} weight="bold" className="text-[#D95800]" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-neutral-600 dark:text-neutral-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Three Audiences */}
        <div className="grid md:grid-cols-3 gap-8 mb-20" data-testid="about-audiences">
          {audiences.map((a) => (
            <div key={a.key} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[6px_6px_0px_#000] flex flex-col" style={{ boxShadow: `6px 6px 0px ${a.color}` }} data-testid={`about-audience-${a.key}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center border-2 border-black" style={{ backgroundColor: a.color }}>
                  <a.icon size={28} weight="bold" className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-neutral-900 dark:text-white">{a.title}</h3>
              </div>
              <ul className="space-y-4 mb-8 flex-1">
                {a.points.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={22} weight="bold" className="flex-shrink-0 mt-0.5" style={{ color: a.color }} />
                    <span className="text-neutral-700 dark:text-neutral-300 font-medium text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(a.to)}
                className="w-full h-13 text-white font-bold text-base border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-[transform,box-shadow,background-color]"
                style={{ backgroundColor: a.color }}
                data-testid={`about-cta-${a.key}`}
              >
                {a.cta}
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
            </div>
          ))}
        </div>

        {/* More Than Hiring */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              More Than a Hiring Platform
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Everything around the job, handled in one place.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="about-platform-extras">
            {platformExtras.map((item, idx) => (
              <button
                key={idx}
                onClick={() => (item.href ? window.open(item.href, '_blank', 'noopener') : navigate(item.to))}
                className="text-left bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] hover:shadow-[4px_4px_0px_#D95800] hover:border-[#D95800] hover:-translate-y-1 transition-[transform,box-shadow,border-color]"
                data-testid={`about-extra-${idx}`}
              >
                <item.icon size={28} weight="bold" className="text-[#D95800] mb-3" />
                <h3 className="font-bold text-neutral-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Our Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              What We Stand For
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: 'Trust', desc: 'Verified profiles and secure payments you can count on' },
              { icon: Lightning, title: 'Speed', desc: 'Connections that happen in minutes, not weeks' },
              { icon: Heart, title: 'Respect', desc: 'Fair treatment for homeowners, contractors and workers alike' },
              { icon: Target, title: 'Results', desc: 'Tools that actually help you get work done' },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-[#D95800]/10 dark:bg-[#D95800]/25 rounded-xl flex items-center justify-center mx-auto mb-4 border-2 border-[#D95800]">
                  <item.icon size={32} weight="bold" className="text-[#D95800]" />
                </div>
                <h3 className="font-bold text-neutral-900 dark:text-white mb-2 text-lg">{item.title}</h3>
                <p className="text-neutral-600 dark:text-neutral-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-neutral-900 dark:bg-neutral-950 rounded-xl p-12 text-center text-white border-2 border-black shadow-[6px_6px_0px_#D95800]">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Build Something Great?
          </h2>
          <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
            Join the platform that's changing how construction gets done.
            Sign up free and see the difference for yourself.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => navigate('/get-quotes')}
              size="lg"
              className="h-16 px-8 bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white font-bold text-lg border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-[transform,box-shadow,background-color]"
              data-testid="about-final-cta-homeowner"
            >
              I'm a Homeowner
            </Button>
            <Button
              onClick={() => navigate('/signup?type=contractor')}
              size="lg"
              className="h-16 px-8 bg-[#EA580C] hover:bg-[#F97316] text-white font-bold text-lg border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-[transform,box-shadow,background-color]"
              data-testid="about-final-cta-contractor"
            >
              I'm a Contractor
            </Button>
            <Button
              onClick={() => navigate('/signup?type=worker')}
              size="lg"
              className="h-16 px-8 bg-[#059669] hover:bg-[#10B981] text-white font-bold text-lg border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-[transform,box-shadow,background-color]"
              data-testid="about-final-cta-worker"
            >
              I'm a Worker
            </Button>
          </div>
          <p className="mt-6 text-neutral-400">
            Free to join. No credit card required.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
